import isNil from "lodash/isNil";

import { DEFAULT_CSV_DELIMITER } from "#shared/constants";
import { formatTolerance } from "#shared/view/components/Thumbnails/AnalysisResult/AnalysisResultComplexitySection";
import type { GdtType, PartComplexity } from "shared";

const escapeCsvField = (value: string | number | null | undefined): string => {
  const stringValue = value === null || value === undefined ? "" : String(value);
  if (/[";,\r\n]/.test(stringValue)) {
    return `"${stringValue.replaceAll('"', '""')}"`;
  }
  return stringValue;
};

const GDT_LABELS: Record<GdtType, string> = {
  perpendicularity: "Perpendicularité",
  flatness: "Planéité",
  symmetry: "Symétrie",
  parallelism: "Parallélisme",
  position: "Position",
  roundness: "Circularité",
  circular_runout: "Battement circulaire",
  cylindricity: "Cylindricité",
  angularity: "Angularité",
  profile_line: "Profil d'une ligne",
  straightness: "Rectitude",
  profile_plane: "Profil d'une surface",
  concentricity: "Concentricité",
  total_runout: "Battement total",
};

type AnyMeasure = NonNullable<PartComplexity["measures"]>[number];

const MEASURE_TYPE_LABELS: Record<AnyMeasure["type"], string> = {
  linear: "Longueur",
  diameter: "Diamètre",
  radius: "Rayon",
  angle: "Angle",
  threading: "Taraudage",
};

const formatMeasureValue = (measure: AnyMeasure): string => {
  switch (measure.type) {
    case "linear":
      return `${measure.length} mm`;
    case "diameter":
      return `Ø${measure.diameter}`;
    case "radius":
      return `R${measure.radius}`;
    case "angle":
      return `${measure.angle}°`;
    case "threading":
      return `M${measure.diameter}`;
  }
};

export const makePartComplexityCsv = (complexity: PartComplexity): string[] => {
  const csvData = [
    ["N°", "Catégorie", "Type", "Valeur", "Tolérance", "Référence"].join(DEFAULT_CSV_DELIMITER),
  ];

  const rows: (string | number)[][] = [];

  complexity.measures?.forEach((measure) => {
    rows.push([
      "Côte",
      MEASURE_TYPE_LABELS[measure.type],
      formatMeasureValue(measure),
      formatTolerance(measure.tolerance),
      "",
    ]);
  });

  complexity.gdts?.forEach((gdt) => {
    rows.push([
      "Contrainte géométrique",
      GDT_LABELS[gdt.type],
      !isNil(gdt.tolerance) ? gdt.tolerance : "",
      "",
      gdt.datum.join(", "),
    ]);
  });

  complexity.roughnesses?.forEach((roughness) => {
    rows.push(["Rugosité", "Ra", roughness.value, "", ""]);
  });

  const numberedRows = rows.map((row, index) =>
    [index + 1, ...row].map(escapeCsvField).join(DEFAULT_CSV_DELIMITER),
  );

  return csvData.concat(numberedRows);
};
